/**
 * @fileoverview Read-only signature preview under the compose / draft body.
 *
 * The send pipeline appends the mailbox signature at send time
 * (`appendSignature` in inkbox actions) — it is not part of the editable
 * TipTap body. Showing it here lets the practitioner see what recipients
 * will get without risking a double-append if they typed it into the body.
 */

import { useQuery } from "convex/react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { plainTextToHtml } from "./htmlBody";
import { normalizeSignatureHtml } from "./signatureHtml";

/**
 * Renders the resolved mailbox signature (override → org default) beneath
 * the email body editor. Returns null when no signature is configured.
 */
export function ComposeSignaturePreview({ jobId }: { jobId: Id<"jobs"> }) {
  const mailbox = useQuery(api.mail.queries.getMailboxForJob, {
    jobId,
  });

  if (mailbox === undefined || mailbox === null) return null;

  // Normalised here as well as on save so signatures stored before paragraph
  // margins were pinned still preview the way they'll be delivered.
  const html = normalizeSignatureHtml(
    mailbox.signatureHtml?.trim() ||
      (mailbox.signatureText?.trim()
        ? plainTextToHtml(mailbox.signatureText)
        : ""),
  );
  if (!html) return null;

  return (
    <div className="border-t px-4 py-3">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Signature · appended on send
      </p>
      {/* Sandboxed so signature markup (tables / images) can't run scripts. */}
      <iframe
        title="Signature preview"
        sandbox=""
        srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;font-family:system-ui,sans-serif;font-size:13px;line-height:1.45;color:#111}table{border-collapse:collapse}img{max-width:100%;height:auto}a{color:#2563eb}</style></head><body>${html}</body></html>`}
        className="h-auto min-h-[48px] w-full bg-transparent"
        // Grow with content — fixed short height clips multi-line signatures.
        onLoad={(e) => {
          const frame = e.currentTarget;
          try {
            const doc = frame.contentDocument;
            if (!doc?.body) return;
            frame.style.height = `${Math.max(doc.body.scrollHeight + 4, 48)}px`;
          } catch {
            // Cross-origin shouldn't happen with srcDoc, but ignore quietly.
          }
        }}
      />
    </div>
  );
}
